import { groupSetsByExercise, calculateSessionStats, isIsometricExercise } from '../utils/ExerciseUtils';

/**
 * Genera suggerimenti di allenamento basati sui dati
 * @param {Array} workouts - Array di allenamenti
 * @param {Array} sets - Array di serie di esercizi
 * @param {Object} trainingAnalyzer - Istanza di TrainingAnalyzer per accedere ai metodi
 * @returns {Array} Suggerimenti generati
 */
export function generateTrainingSuggestions(workouts, sets, trainingAnalyzer) {
  const suggestions = [];

  // Analisi per frequenza di allenamento
  addFrequencySuggestions(workouts, suggestions);

  // Raggruppa le serie per esercizio
  const exerciseMap = groupSetsByExercise(sets);

  // Analisi per ogni esercizio
  addExerciseSpecificSuggestions(exerciseMap, suggestions, trainingAnalyzer);

  return suggestions;
}

/**
 * Aggiunge suggerimenti basati sulla frequenza di allenamento
 * @param {Array} workouts - Array di allenamenti
 * @param {Array} suggestions - Array di suggerimenti da popolare
 */
function addFrequencySuggestions(workouts, suggestions) {
  if (workouts.length >= 2) {
    const dates = workouts.map(w => new Date(w.data_allenamento));
    const daysBetweenWorkouts = [];
    
    for (let i = 1; i < dates.length; i++) {
      const diffTime = Math.abs(dates[i-1] - dates[i]);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      daysBetweenWorkouts.push(diffDays);
    }
    
    const averageDays = daysBetweenWorkouts.length > 0 
      ? daysBetweenWorkouts.reduce((sum, days) => sum + days, 0) / daysBetweenWorkouts.length 
      : 0;
    
    // Suggerimento sulla frequenza
    if (averageDays > 4) {
      suggestions.push({
        type: 'frequency',
        title: 'Aumenta la frequenza',
        description: `Ti alleni ogni ${averageDays.toFixed(1)} giorni. Prova ad aumentare a 3-4 volte/settimana.`,
        icon: 'RefreshCw',
        priority: 'medium',
        actionable: true,
        suggestedAction: 'Aggiungi un allenamento aggiuntivo a settimana e distribuisci meglio i gruppi muscolari'
      });
    }
  }
}

/**
 * Aggiunge suggerimenti specifici per ogni esercizio
 * @param {Object} exerciseMap - Map di esercizi con le relative serie
 * @param {Array} suggestions - Array di suggerimenti da popolare
 * @param {Object} trainingAnalyzer - Istanza di TrainingAnalyzer
 */
function addExerciseSpecificSuggestions(exerciseMap, suggestions, trainingAnalyzer) {
  for (const exerciseId in exerciseMap) {
    const exercise = exerciseMap[exerciseId];
    
    if (exercise.sets.length < 2) continue;
    
    // Raggruppa i set per data di allenamento
    const setsByWorkout = {};
    exercise.sets.forEach(set => {
      const workoutDate = set.date.toISOString().split('T')[0];
      if (!setsByWorkout[workoutDate]) {
        setsByWorkout[workoutDate] = [];
      }
      setsByWorkout[workoutDate].push(set);
    });

    // Converti in array di date ordinate
    const workoutDates = Object.keys(setsByWorkout).sort();
    
    // Se non ci sono almeno 2 allenamenti, passa al prossimo esercizio
    if (workoutDates.length < 2) continue;

    const firstWorkoutSets = setsByWorkout[workoutDates[0]];
    const lastWorkoutSets = setsByWorkout[workoutDates[workoutDates.length - 1]];

    // Calcola 1RM, peso massimo e peso medio
    const firstWorkoutStats = calculateSessionStats(firstWorkoutSets);
    const lastWorkoutStats = calculateSessionStats(lastWorkoutSets);
    
    // Calcola il cambiamento percentuale nel 1RM
    const rmChange = lastWorkoutStats.estimatedRM - firstWorkoutStats.estimatedRM;
    const rmChangePercent = firstWorkoutStats.estimatedRM > 0 
      ? (rmChange / firstWorkoutStats.estimatedRM) * 100 
      : 0;
      
    // Formatta correttamente
    const formatWeight = (weight) => {
      return weight < 10 ? `${weight.toFixed(1)} kg` : `${weight.toFixed(0)} kg`;
    };
    
    const formattedCurrentWeight = lastWorkoutStats.maxWeight < 10 
      ? `${lastWorkoutStats.maxWeight.toFixed(1)} kg` 
      : `${lastWorkoutStats.maxWeight.toFixed(0)} kg`;
    
    // Calcola un range di peso consigliato
    const weightRange = trainingAnalyzer.calculateWeightRange(lastWorkoutStats.maxWeight, exercise.type);
      
    // Verifica se c'è stato un aumento significativo (basato sul 1RM)
    if (rmChangePercent >= 10) {
      suggestions.push({
        type: 'progress',
        exerciseName: exercise.name,
        title: 'Ottimo progresso!',
        description: `Hai aumentato la forza del ${rmChangePercent.toFixed(1)}% in ${exercise.name}`,
        icon: 'TrendingUp',
        priority: 'medium',
        actionable: true,
        suggestedAction: `Continua con incrementi graduali del 5-10% per ${exercise.name}`,
        data: {
          currentWeight: formattedCurrentWeight,
          maxWeight: formatWeight(lastWorkoutStats.maxWeight),
          averageWeight: formatWeight(lastWorkoutStats.averageWeight),
          estimatedRM: formatWeight(lastWorkoutStats.estimatedRM),
          percentage: `+${rmChangePercent.toFixed(1)}%`
        }
      });
    }
    
    // Verifica plateau tra gli allenamenti
    checkForPlateau(workoutDates, setsByWorkout, exercise, formattedCurrentWeight, weightRange, suggestions);
  }
}

/**
 * Rileva modifiche recenti nella strategia di allenamento
 * @param {Array} lastTwoWorkoutDates - Ultime due date di allenamento
 * @param {Object} setsByWorkout - Serie raggruppate per data di allenamento
 * @returns {Object} Informazioni sui cambiamenti di strategia
 */
function detectRecentStrategyChange(lastTwoWorkoutDates, setsByWorkout) {
  if (lastTwoWorkoutDates.length < 2) return { hasChanged: false };
  
  const lastDate = lastTwoWorkoutDates[lastTwoWorkoutDates.length - 1];
  const previousDate = lastTwoWorkoutDates[lastTwoWorkoutDates.length - 2];
  
  const lastWorkoutSets = setsByWorkout[lastDate];
  const previousWorkoutSets = setsByWorkout[previousDate];
  
  // Calcola i valori medi per l'ultima sessione e quella precedente
  const avgLastWeight = lastWorkoutSets.reduce((sum, set) => sum + (parseFloat(set.weight) || 0), 0) / lastWorkoutSets.length;
  const avgLastReps = lastWorkoutSets.reduce((sum, set) => sum + (parseInt(set.reps) || 0), 0) / lastWorkoutSets.length;
  
  const avgPrevWeight = previousWorkoutSets.reduce((sum, set) => sum + (parseFloat(set.weight) || 0), 0) / previousWorkoutSets.length;
  const avgPrevReps = previousWorkoutSets.reduce((sum, set) => sum + (parseInt(set.reps) || 0), 0) / previousWorkoutSets.length;
  
  // Calcola le variazioni percentuali
  const weightChangePercent = (avgLastWeight - avgPrevWeight) / avgPrevWeight * 100;
  const repsChangePercent = (avgLastReps - avgPrevReps) / avgPrevReps * 100;
  
  // Verifica se c'è stato un cambiamento significativo nella strategia
  const significantWeightIncrease = weightChangePercent >= 5;  // Aumento del peso ≥ 5%
  const significantRepsIncrease = repsChangePercent >= 15;     // Aumento delle ripetizioni ≥ 15%
  const strategyShift = (weightChangePercent > 0 && repsChangePercent < -5) || 
                         (weightChangePercent < -5 && repsChangePercent > 10);
  
  const hasChanged = significantWeightIncrease || significantRepsIncrease || strategyShift;
  
  return {
    hasChanged,
    weightChangePercent,
    repsChangePercent,
    isWeightFocused: weightChangePercent > 0 && weightChangePercent > repsChangePercent,
    isRepsFocused: repsChangePercent > 0 && repsChangePercent > weightChangePercent,
    details: {
      avgLastWeight,
      avgLastReps,
      avgPrevWeight,
      avgPrevReps
    }
  };
}

/**
 * Verifica se un esercizio è in plateau
 * @param {Array} workoutDates - Date di allenamento ordinate
 * @param {Object} setsByWorkout - Serie raggruppate per data di allenamento
 * @param {Object} exercise - Informazioni sull'esercizio
 * @param {string} formattedCurrentWeight - Peso attuale formattato
 * @param {Object} weightRange - Range di peso consigliato
 * @param {Array} suggestions - Array di suggerimenti da popolare
 */
function checkForPlateau(workoutDates, setsByWorkout, exercise, formattedCurrentWeight, weightRange, suggestions) {
  // Verifica se ci sono abbastanza dati per analizzare un plateau
  if (workoutDates.length < 3) return;
  
  // Prendi le ultime date di allenamento (estendi da 3 a 4 per maggiore accuratezza)
  const lastWorkoutDates = workoutDates.slice(-4);
  
  // Verifica se c'è stato un cambiamento recente nella strategia
  const strategyChange = detectRecentStrategyChange(lastWorkoutDates.slice(-2), setsByWorkout);
  
  // Se c'è stato un cambiamento significativo recente nella strategia, non segnalare plateau
  if (strategyChange.hasChanged) {
    // L'utente ha già modificato la sua strategia, non segnalare un plateau
    return;
  }
  
  // Identifica se l'esercizio è a corpo libero o isometrico
  const isBodyweight = setsByWorkout[lastWorkoutDates[0]].every(set => parseFloat(set.weight) === 0);
  const isIsometricExe = isIsometricExercise(exercise.name);
  
  // Calcola valori appropriati in base al tipo di esercizio
  const performanceValues = lastWorkoutDates.map(date => {
    const workoutSets = setsByWorkout[date];
    
    if (isIsometricExe) {
      // Per esercizi isometrici (come plank), usa la durata massima
      return Math.max(...workoutSets.map(set => parseInt(set.reps) || 0));
    } else if (isBodyweight) {
      // Per esercizi a corpo libero, usa il numero massimo di ripetizioni
      return Math.max(...workoutSets.map(set => parseInt(set.reps) || 0));
    } else {
      // Per esercizi con peso, usa il 1RM stimato
      const stats = calculateSessionStats(workoutSets);
      return stats.estimatedRM;
    }
  });
  
  const averagePerformance = performanceValues.reduce((sum, val) => sum + val, 0) / performanceValues.length;
  
  // Algoritmo di rilevamento plateau:
  // 1. Controllo se tutti i valori sono entro il 5% dalla media (plateau stretto)
  const isStrictPlateau = performanceValues.every(val => 
    Math.abs(val - averagePerformance) / averagePerformance < 0.05
  );
  
  // 2. Controllo se non c'è stato un aumento significativo (plateau più lungo)
  let isLongPlateau = false;
  if (performanceValues.length >= 3) {
    // Verifica se non c'è un trend crescente
    const firstHalfAvg = (performanceValues[0] + (performanceValues[1] || performanceValues[0])) / 2;
    const secondHalfAvg = (performanceValues[performanceValues.length - 2] + performanceValues[performanceValues.length - 1]) / 2;
    isLongPlateau = (secondHalfAvg - firstHalfAvg) / firstHalfAvg < 0.02; // Meno del 2% di aumento
  }
  
  // Se è rilevato un plateau, crea suggerimenti dettagliati
  if (isStrictPlateau || isLongPlateau) {
    // Ottieni statistiche dell'ultima sessione
    const lastSessionStats = isIsometricExe || isBodyweight 
      ? { maxPerformance: performanceValues[performanceValues.length - 1] }
      : calculateSessionStats(setsByWorkout[lastWorkoutDates[lastWorkoutDates.length - 1]]);
    
    // Analizza i pesi e le ripetizioni delle ultime due sessioni per offrire consigli mirati
    const lastSessionSets = setsByWorkout[lastWorkoutDates[lastWorkoutDates.length - 1]];
    const prevSessionSets = setsByWorkout[lastWorkoutDates[lastWorkoutDates.length - 2]];
    
    const lastAvgWeight = lastSessionSets.reduce((sum, set) => sum + (parseFloat(set.weight) || 0), 0) / lastSessionSets.length;
    const lastAvgReps = lastSessionSets.reduce((sum, set) => sum + (parseInt(set.reps) || 0), 0) / lastSessionSets.length;
    
    const prevAvgWeight = prevSessionSets ? prevSessionSets.reduce((sum, set) => sum + (parseFloat(set.weight) || 0), 0) / prevSessionSets.length : 0;
    const prevAvgReps = prevSessionSets ? prevSessionSets.reduce((sum, set) => sum + (parseInt(set.reps) || 0), 0) / prevSessionSets.length : 0;
    
    // Personalizza le strategie di breakthrough in base ai pattern recenti
    let primaryBreakthroughStrategy = "";
    
    if (lastAvgWeight === prevAvgWeight && lastAvgReps === prevAvgReps) {
      // Se sia peso che ripetizioni sono rimasti costanti
      primaryBreakthroughStrategy = "Aumenta il peso del 5-10% mantenendo ripetizioni più basse";
    } else if (Math.abs(lastAvgWeight - prevAvgWeight) < 0.1) {
      // Se il peso è rimasto quasi uguale
      primaryBreakthroughStrategy = "Aumenta il peso e riduci temporaneamente le ripetizioni";
    } else if (Math.abs(lastAvgReps - prevAvgReps) < 0.5) {
      // Se le ripetizioni sono rimaste quasi uguali
      primaryBreakthroughStrategy = "Mantieni il peso e aumenta le ripetizioni del 10-20%";
    }
    
    // Formatta i pesi o le performance
    const formatPerformance = (value) => {
      if (isIsometricExe) {
        return `${value} sec`;
      } else if (isBodyweight) {
        return `${value} ripetizioni`;
      } else {
        return value < 10 ? `${value.toFixed(1)} kg` : `${value.toFixed(0)} kg`;
      }
    };
    
    // Determina il tipo di plateau
    const plateauType = isStrictPlateau ? 'stretto' : 'lungo';
    const plateauIntensity = isStrictPlateau ? 'alto' : 'moderato';
    
    // Genera suggerimenti per superare il plateau basati sul tipo di esercizio
    let breakThroughStrategies = [];
    
    if (isIsometricExe) {
      breakThroughStrategies = [
        "Aumenta progressivamente la durata di 5-10 secondi",
        "Prova una variante più difficile (es. solleva un arto)",
        "Aggiungi piccole pulsazioni durante la tenuta"
      ];
    } else if (isBodyweight) {
      breakThroughStrategies = [
        "Aumenta il numero di ripetizioni del 10-20%",
        "Rallenta la fase eccentrica (3-4 secondi)",
        "Aggiungi una breve pausa isometrica in posizione difficile"
      ];
    } else if (isStrictPlateau) {
      breakThroughStrategies = [
        primaryBreakthroughStrategy || "Cambia il range di ripetizioni per 1-2 settimane",
        "Prova con serie discendenti (drop set)",
        "Modifica il tempo sotto tensione (esecuzione più lenta)"
      ];
    } else {
      breakThroughStrategies = [
        primaryBreakthroughStrategy || "Introduci una settimana di scarico e poi incrementa il peso",
        "Aggiungi una serie extra con peso ridotto",
        "Modifica leggermente la tecnica o l'angolo di lavoro"
      ];
    }
    
    // Suggerimento per prossima progressione
    let nextProgressionSuggestion;
    if (isIsometricExe) {
      const currentTime = lastSessionStats.maxPerformance;
      nextProgressionSuggestion = `${currentTime + 10} sec`;
    } else if (isBodyweight) {
      const currentReps = lastSessionStats.maxPerformance;
      nextProgressionSuggestion = `${Math.ceil(currentReps * 1.1)} ripetizioni`;
    } else {
      nextProgressionSuggestion = weightRange.formatted;
    }
    
    // Aggiungi un suggerimento specifico per il plateau
    suggestions.push({
      type: 'plateau',
      exerciseName: exercise.name,
      title: `Plateau ${plateauType} rilevato`,
      description: `La tua performance per ${exercise.name} è rimasta stabile nelle ultime ${plateauIntensity === 'alto' ? '3-4' : '4+' } sessioni`,
      icon: 'Scale',
      priority: 'medium',
      actionable: true,
      suggestedAction: `Prova una di queste strategie: ${breakThroughStrategies[0]}`,
      data: {
        currentWeight: isIsometricExe || isBodyweight 
          ? formatPerformance(performanceValues[performanceValues.length - 1])
          : formattedCurrentWeight,
        maxWeight: isIsometricExe || isBodyweight
          ? formatPerformance(Math.max(...performanceValues))
          : formatPerformance(lastSessionStats.maxWeight),
        averageWeight: isIsometricExe || isBodyweight
          ? formatPerformance(Math.round(averagePerformance))
          : formatPerformance(lastSessionStats.averageWeight),
        estimatedRM: isIsometricExe || isBodyweight
          ? formatPerformance(Math.max(...performanceValues))
          : formatPerformance(lastSessionStats.estimatedRM),
        suggestedWeight: isIsometricExe || isBodyweight
          ? nextProgressionSuggestion
          : weightRange.formatted,
        plateauType: plateauType,
        plateauIntensity: plateauIntensity,
        breakThroughStrategies: breakThroughStrategies
      }
    });
  }
}

/**
 * Genera suggerimenti di bilanciamento muscolare
 * @param {Array} sets - Serie di esercizi
 * @returns {Array} Suggerimenti per bilanciamento muscolare
 */
export function generateMuscleBalanceSuggestions(sets) {
  const suggestions = [];
  
  // Calcola quante volte viene allenato ogni gruppo muscolare
  const muscleGroupCounts = {};
  sets.forEach(set => {
    const muscleGroup = set.gruppo_muscolare || 'Altro';
    muscleGroupCounts[muscleGroup] = (muscleGroupCounts[muscleGroup] || 0) + 1;
  });
  
  // Converti in array ordinato
  const muscleGroups = Object.entries(muscleGroupCounts)
    .map(([group, count]) => ({ group, count }))
    .sort((a, b) => b.count - a.count);
  
  if (muscleGroups.length > 1) {
    const maxCount = muscleGroups[0].count;
    const minCount = muscleGroups[muscleGroups.length - 1].count;
    
    // Se il gruppo meno allenato ha meno del 40% del gruppo più allenato
    if (minCount < maxCount * 0.4) {
      // Identifica i gruppi muscolari più trascurati
      const neglectedGroups = muscleGroups.filter(mg => mg.count < maxCount * 0.4);
      
      // Genera suggerimenti per ciascun gruppo trascurato (limita a 1 suggerimento)
      if (neglectedGroups.length > 0) {
        const mg = neglectedGroups[0];
        // Trova esercizi specifici da suggerire
        const exerciseSuggestions = getExerciseSuggestionsForMuscleGroup(mg.group);
        
        suggestions.push({
          type: 'muscle_balance',
          title: `${mg.group} sottosviluppato`,
          description: `Il volume per ${mg.group} è solo ${((mg.count / maxCount) * 100).toFixed(0)}% rispetto al gruppo più allenato (${muscleGroups[0].group}).`,
          icon: 'BarChart2',
          priority: 'medium',
          actionable: true,
          suggestedAction: `Aggiungi 2-3 serie di ${exerciseSuggestions[0]} alla tua prossima sessione`,
          data: {
            leastTrainedGroup: mg.group,
            currentTrainingVolume: mg.count,
            mostTrainedGroup: muscleGroups[0].group,
            mostTrainedVolume: muscleGroups[0].count,
            percentageOfMax: ((mg.count / maxCount) * 100).toFixed(0) + '%'
          },
          recommendations: [
            `Aggiungi 1-2 esercizi specifici per ${mg.group}`,
            `Esercizi consigliati: ${exerciseSuggestions.join(', ')}`,
            `Dedica una sessione specifica a ${mg.group} nel tuo programma settimanale`
          ]
        });
      }
    }
    
    // Verifica la presenza di gruppi muscolari importanti mancanti
    const importantMuscleGroups = ['Petto', 'Schiena', 'Gambe', 'Spalle'];
    const presentGroups = new Set(muscleGroups.map(mg => mg.group));
    const missingGroups = importantMuscleGroups.filter(group => !presentGroups.has(group));
    
    if (missingGroups.length > 0) {
      const recommendedExercises = missingGroups.map(group => {
        const exercises = getExerciseSuggestionsForMuscleGroup(group);
        return `${group}: ${exercises.slice(0, 2).join(', ')}`;
      });
      
      suggestions.push({
        type: 'muscle_missing',
        title: 'Gruppi muscolari importanti mancanti',
        description: `Non stai allenando: ${missingGroups.join(', ')}. Questi gruppi sono fondamentali per uno sviluppo completo.`,
        icon: 'AlertCircle',
        priority: 'high',
        actionable: true,
        suggestedAction: `Aggiungi un allenamento specifico per ${missingGroups[0]} alla tua routine`,
        data: {
          missingGroups
        },
        recommendations: [
          `Considera di aggiungere questi gruppi al tuo programma di allenamento`,
          ...recommendedExercises.slice(0, 2)
        ]
      });
    }
  }

  return suggestions;
}

/**
 * Fornisce esercizi suggeriti per un gruppo muscolare specifico
 * @param {string} muscleGroup - Nome del gruppo muscolare
 * @returns {Array} Array di esercizi suggeriti
 */
function getExerciseSuggestionsForMuscleGroup(muscleGroup) {
  const exerciseSuggestions = {
    'Petto': ['Panca Piana', 'Panca Inclinata', 'Croci con Manubri', 'Dip'],
    'Schiena': ['Trazioni', 'Rematore', 'Pulley', 'Pullover'],
    'Gambe': ['Squat', 'Leg Press', 'Leg Extension', 'Leg Curl', 'Affondi'],
    'Spalle': ['Military Press', 'Alzate Laterali', 'Alzate Frontali', 'Rear Delt'],
    'Bicipiti': ['Curl con Bilanciere', 'Curl con Manubri', 'Curl Martello', 'Curl Concentrato'],
    'Tricipiti': ['French Press', 'Pushdown', 'Dip', 'Kick-back'],
    'Addominali': ['Crunch', 'Plank', 'Leg Raise', 'Russian Twist']
  };
  
  return exerciseSuggestions[muscleGroup] || ['Consulta un personal trainer per esercizi specifici'];
}

/**
 * Analizza e genera suggerimenti per la periodizzazione dell'allenamento
 * @param {Array} workouts - Array di allenamenti
 * @param {Array} sets - Array di serie di esercizi
 * @returns {Array} Suggerimenti di periodizzazione
 */
export function generatePeriodizationSuggestions(workouts, sets) {
  if (!workouts || workouts.length < 8) {
    return []; // Dati insufficienti per suggerimenti di periodizzazione
  }

  const suggestions = [];
  
  // Ordina gli allenamenti per data
  const sortedWorkouts = [...workouts].sort((a, b) => 
    new Date(a.data_allenamento) - new Date(b.data_allenamento)
  );
  
  // Calcola il numero di settimane coperte dagli allenamenti
  const firstWorkoutDate = new Date(sortedWorkouts[0].data_allenamento);
  const lastWorkoutDate = new Date(sortedWorkouts[sortedWorkouts.length - 1].data_allenamento);
  const weeksCovered = Math.ceil((lastWorkoutDate - firstWorkoutDate) / (7 * 24 * 60 * 60 * 1000));
  
  // Se sono passate più di 8 settimane senza una settimana di scarico evidente
  if (weeksCovered >= 8) {
    // Raggruppa i workout per settimana per identificare il volume settimanale
    const weeklyVolumes = {};
    
    sortedWorkouts.forEach(workout => {
      const workoutDate = new Date(workout.data_allenamento);
      // Calcola l'indice della settimana rispetto alla prima data
      const weekIndex = Math.floor((workoutDate - firstWorkoutDate) / (7 * 24 * 60 * 60 * 1000));
      
      if (!weeklyVolumes[weekIndex]) {
        weeklyVolumes[weekIndex] = {
          totalSets: 0,
          workoutCount: 0
        };
      }
      
      // Calcola il numero di serie per workout
      const workoutSets = sets.filter(set => set.allenamento_id == workout.id);
      weeklyVolumes[weekIndex].totalSets += workoutSets.length;
      weeklyVolumes[weekIndex].workoutCount++;
    });
    
    // Converti in array e ordina per settimana
    const weeklyVolumeArray = Object.entries(weeklyVolumes)
      .map(([week, data]) => ({
        week: parseInt(week),
        ...data
      }))
      .sort((a, b) => a.week - b.week);
    
    // Verifica se c'è una settimana di scarico evidente
    let hasDeloadWeek = false;
    
    // Una settimana di scarico tipicamente ha volume ridotto di circa 40%+
    if (weeklyVolumeArray.length > 4) {
      for (let i = 4; i < weeklyVolumeArray.length; i++) {
        const currentWeek = weeklyVolumeArray[i];
        // Calcola media delle 4 settimane precedenti
        const previousWeeks = weeklyVolumeArray.slice(i-4, i);
        const avgPreviousSets = previousWeeks.reduce((sum, w) => sum + w.totalSets, 0) / 4;
        
        // Se il volume è significativamente più basso, considerala una settimana di scarico
        if (currentWeek.totalSets < avgPreviousSets * 0.6) {
          hasDeloadWeek = true;
          break;
        }
      }
    }
    
    if (!hasDeloadWeek) {
      suggestions.push({
        type: 'periodization',
        title: 'Periodizzazione: Settimana di Scarico',
        description: 'Stai allenandoti ad alto volume da 8+ settimane senza una chiara settimana di scarico. Questo potrebbe portare a sovrallenamento e plateaus.',
        icon: 'RefreshCw',
        priority: 'medium',
        actionable: true,
        suggestedAction: 'Pianifica una settimana di scarico riducendo il volume del 40-50% ma mantenendo l\'intensità',
        data: {
          weeksCovered: weeksCovered,
          averageWeeklyWorkouts: (sortedWorkouts.length / weeksCovered).toFixed(1)
        },
        recommendations: [
          'Pianifica una settimana di scarico riducendo il volume del 40-50%',
          'Mantieni l\'intensità (peso) ma riduci il numero di serie',
          'Dopo la settimana di scarico, inizia un nuovo ciclo aumentando progressivamente il volume'
        ]
      });
    }
  }
  
  // Verifica variazione del carico (se c'è progressione o se è sempre lo stesso)
  const exerciseProgression = {};
  
  // Raggruppa le serie per esercizio
  sets.forEach(set => {
    const exerciseName = set.esercizio_nome;
    if (!exerciseName) return;
    
    if (!exerciseProgression[exerciseName]) {
      exerciseProgression[exerciseName] = [];
    }
    
    exerciseProgression[exerciseName].push({
      date: new Date(set.timestamp || set.data_allenamento),
      weight: parseFloat(set.peso) || 0,
      reps: parseInt(set.ripetizioni) || 0
    });
  });
  
  // Identifica esercizi che mostrano mancanza di progressione lineare
  const stagnantExercises = [];
  
  for (const [exercise, setData] of Object.entries(exerciseProgression)) {
    // Considera solo esercizi con almeno 4 occorrenze
    if (setData.length >= 4) {
      // Ordina per data
      setData.sort((a, b) => a.date - b.date);
      
      // Dividi in due metà: prima metà e seconda metà
      const midpoint = Math.floor(setData.length / 2);
      const firstHalf = setData.slice(0, midpoint);
      const secondHalf = setData.slice(midpoint);
      
      // Calcola il peso medio per entrambe le metà
      const avgWeightFirstHalf = firstHalf.reduce((sum, s) => sum + s.weight, 0) / firstHalf.length;
      const avgWeightSecondHalf = secondHalf.reduce((sum, s) => sum + s.weight, 0) / secondHalf.length;
      
      // Se la crescita è molto bassa (meno del 2.5%) ma non è un plateau totale
      const growthPercentage = ((avgWeightSecondHalf - avgWeightFirstHalf) / avgWeightFirstHalf) * 100;
      
      if (growthPercentage >= 0 && growthPercentage < 2.5) {
        stagnantExercises.push({
          name: exercise,
          growthPercentage: growthPercentage.toFixed(1),
          occurrences: setData.length
        });
      }
    }
  }
  
  if (stagnantExercises.length >= 3) {
    suggestions.push({
      type: 'periodization',
      title: 'Periodizzazione Ondulata Consigliata',
      description: 'Diversi esercizi mostrano una progressione lineare molto lenta. Una periodizzazione ondulata potrebbe stimolare nuovi progressi.',
      icon: 'Activity',
      priority: 'medium',
      actionable: true,
      suggestedAction: 'Alterna settimane di alto volume (10-15 rep) con settimane di alta intensità (4-6 rep)',
      data: {
        stagnantExercises: stagnantExercises.slice(0, 3).map(e => e.name)
      },
      recommendations: [
        'Alterna settimane di alto volume (10-15 rep) con settimane di alta intensità (4-6 rep)',
        'Varia il carico di settimana in settimana: +10% una settimana, -5% la successiva',
        'Considera di modificare leggermente gli esercizi (cambia presa, angolazione, ecc.)'
      ]
    });
  }
  
  return suggestions;
}